import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useCurrency } from "@/context/CurrencyContext";
import { fmtFull, toLocalInput } from "@/lib/dates";
import { CalendarCheck, Download, Instagram, Plus, Trash2, Users } from "lucide-react";

const showErr = (e) => toast.error(formatApiError(e.response?.data?.detail));
const inputCls = "bg-zinc-950 border-zinc-800";

function AdminField({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-[0.2em] font-adult-mono text-zinc-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Empty({ children, testid }) {
  return <div data-testid={testid} className="rounded-2xl border border-dashed border-zinc-800 p-10 text-center text-sm text-zinc-400">{children}</div>;
}

const FILTERS = [["all", "All"], ["paid", "Paid"], ["unpaid", "Not paid yet"], ["leads", "Didn't sign in"]];
const PILL = {
  confirmed: "bg-emerald-500/10 border-emerald-500/40 text-emerald-300",
  paid: "bg-amber-500/10 border-amber-500/40 text-amber-300",
  unpaid: "bg-zinc-800 border-zinc-700 text-zinc-400",
  lead: "bg-violet-500/10 border-violet-500/40 text-violet-300",
};

function Pill({ kind, children }) {
  return <span className={`shrink-0 text-[10px] uppercase tracking-[0.16em] font-adult-mono rounded-full border px-2.5 py-1 ${PILL[kind]}`}>{children}</span>;
}

export function OneOnOneAdminTab() {
  const [data, setData] = useState({ bookings: [], leads: [] });
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/admin/one-on-one").then((r) => setData(r.data)).catch(showErr);
  useEffect(() => { load(); }, []);
  const paid = data.bookings.filter((b) => b.payment_status === "paid");
  const unpaid = data.bookings.filter((b) => b.payment_status !== "paid");
  const counts = { all: data.bookings.length + data.leads.length, paid: paid.length, unpaid: unpaid.length, leads: data.leads.length };
  const bookings = { all: data.bookings, paid, unpaid, leads: [] }[filter];
  const leads = filter === "all" || filter === "leads" ? data.leads : [];
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-zinc-300 flex gap-3">
        <CalendarCheck className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <span>Customers share their preferred days and time. For paid bookings, set the confirmed time (and call link). The customer is emailed straight away and gets a reminder 24 hours before.</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k} data-testid={`admin-1on1-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-full border px-3.5 py-1.5 text-xs font-adult-mono transition-colors ${filter === k ? "border-amber-500 bg-amber-500/15 text-amber-200" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>{l} · {counts[k]}</button>
        ))}
      </div>
      {bookings.length + leads.length === 0 ? <Empty testid="admin-1on1-empty">Nothing here yet.</Empty> : (
        <div className="grid xl:grid-cols-2 gap-4">
          {bookings.map((b) => <BookingCard key={b.booking_id} b={b} onSaved={load} />)}
          {leads.map((l) => <LeadCard key={l.request_id} l={l} />)}
        </div>
      )}
    </div>
  );
}

function Details({ item }) {
  const rows = [["Age", item.age], ["Country", item.country], ["Phone", item.phone], ["Topic", item.reason_label], ["Prefers", item.preferred_label]].filter(([, v]) => v);
  return (
    <>
      <dl className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        {rows.map(([k, v]) => (
          <div key={k} className={k === "Prefers" ? "sm:col-span-2" : ""}><dt className="inline text-zinc-500">{k}: </dt><dd className="inline text-zinc-200">{v}</dd></div>
        ))}
      </dl>
      {item.notes && <p className="mt-3 text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{item.notes}</p>}
    </>
  );
}

function BookingCard({ b, onSaved }) {
  const { format } = useCurrency();
  const paid = b.payment_status === "paid";
  const kind = !paid ? "unpaid" : b.scheduled_start_iso ? "confirmed" : "paid";
  const label = { unpaid: "Not paid yet", paid: "Paid · needs a time", confirmed: "Confirmed" }[kind];
  return (
    <div data-testid={`admin-1on1-${b.booking_id}`} className={`rounded-2xl border p-5 ${paid ? "border-zinc-800" : "border-dashed border-zinc-800 bg-zinc-950/40"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-adult-heading text-lg">{b.name || b.customer?.name || "—"}</div>
          <a href={`mailto:${b.customer?.email}`} className="text-xs text-amber-400 hover:underline break-all">{b.customer?.email}</a>
        </div>
        <Pill kind={kind}>{label}</Pill>
      </div>
      <Details item={b} />
      <div className="mt-3 text-[11px] font-adult-mono text-zinc-500">
        {paid ? `Paid ${format(b.amount, b.currency)}` : `Started checkout, ${format(b.amount, b.currency)} not paid`} · {new Date(b.created_at).toLocaleString()}
      </div>
      {paid && <ConfirmTime b={b} onSaved={onSaved} />}
    </div>
  );
}

function LeadCard({ l }) {
  return (
    <div data-testid={`admin-1on1-lead-${l.request_id}`} className="rounded-2xl border border-dashed border-violet-500/30 bg-violet-500/[0.03] p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="font-adult-heading text-lg">{l.name}</div>
        <Pill kind="lead">Didn&apos;t sign in</Pill>
      </div>
      <Details item={l} />
      <div className="mt-3 text-[11px] font-adult-mono text-zinc-500">Filled in the form but didn&apos;t sign in or pay · {new Date(l.created_at).toLocaleString()}</div>
    </div>
  );
}

function ConfirmTime({ b, onSaved }) {
  const [start, setStart] = useState(toLocalInput(b.scheduled_start_iso));
  const [link, setLink] = useState(b.call_link || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!start) { toast.error("Pick the confirmed date and time"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/one-on-one/${b.booking_id}`, { scheduled_start_iso: new Date(start).toISOString(), call_link: link.trim() || null });
      toast.success("Saved. The customer has been emailed.");
      onSaved();
    } catch (e) { showErr(e); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 rounded-xl border border-zinc-800 bg-black/30 p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] font-adult-mono text-amber-400 mb-3 flex items-center gap-2">
        <CalendarCheck className="w-3.5 h-3.5" />{b.scheduled_start_iso ? `Confirmed · ${fmtFull(b.scheduled_start_iso)}` : "Confirm a time"}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <AdminField label="Date & time (your time zone)"><Input type="datetime-local" data-testid={`admin-1on1-start-${b.booking_id}`} value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} /></AdminField>
        <AdminField label="Call link (optional)"><Input data-testid={`admin-1on1-link-${b.booking_id}`} value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://meet.google.com/…" className={`${inputCls} font-adult-mono text-xs`} /></AdminField>
      </div>
      <Button data-testid={`admin-1on1-save-${b.booking_id}`} disabled={busy} onClick={save} className="mt-3 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full">
        {busy ? "Saving…" : b.scheduled_start_iso ? "Update & email customer" : "Confirm & email customer"}
      </Button>
    </div>
  );
}

export function WorkshopsAdminTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/workshops").then((r) => setItems(r.data)).catch(showErr);
  useEffect(() => { load(); }, []);
  return <div className="grid lg:grid-cols-2 gap-6">{items.map((w) => <WorkshopAdminCard key={w.workshop_id} w={w} onSaved={load} />)}</div>;
}

function WorkshopAdminCard({ w, onSaved }) {
  const [start, setStart] = useState(toLocalInput(w.start_time_iso));
  const [duration, setDuration] = useState(w.duration_min || "");
  const [link, setLink] = useState(w.join_link || "");
  const save = async (clear) => {
    const body = clear ? { start_time_iso: null } : { start_time_iso: start ? new Date(start).toISOString() : null, duration_min: duration ? Number(duration) : null, join_link: link.trim() };
    try {
      await api.patch(`/admin/workshops/${w.workshop_id}`, body);
      if (clear) setStart("");
      toast.success(clear ? "Date cleared. The page now says 'Date announced soon'" : "Workshop updated");
      onSaved();
    } catch (e) { showErr(e); }
  };
  return (
    <div data-testid={`admin-workshop-${w.workshop_id}`} className="rounded-2xl border border-zinc-800 p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-adult-heading text-xl">{w.title}</div>
          <div className="text-xs font-adult-mono text-zinc-500 mt-1">₹{w.price_inr} · ${w.price_usd} · /workshops/{w.workshop_id}</div>
        </div>
        <span className="text-xs font-adult-mono rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-2.5 py-1 inline-flex items-center gap-1"><Users className="w-3 h-3" />{w.enrollees.length}</span>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-3">
        <AdminField label="Date & time" className="col-span-2"><Input type="datetime-local" data-testid={`admin-workshop-start-${w.workshop_id}`} value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} /></AdminField>
        <AdminField label="Minutes"><Input type="number" min={15} data-testid={`admin-workshop-duration-${w.workshop_id}`} value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="90" className={`${inputCls} font-adult-mono`} /></AdminField>
      </div>
      <AdminField label="Joining link (sent in the reminder email)" className="mt-3">
        <Input data-testid={`admin-workshop-link-${w.workshop_id}`} value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://zoom.us/j/…" className={`${inputCls} font-adult-mono text-xs`} />
      </AdminField>
      <p className="mt-2 text-[11px] text-zinc-500">Enrolled learners get a reminder email 24 hours before the workshop.</p>
      <div className="mt-4 flex gap-2">
        <Button data-testid={`admin-workshop-save-${w.workshop_id}`} onClick={() => save(false)} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full">Save</Button>
        {w.start_time_iso && <Button variant="ghost" data-testid={`admin-workshop-clear-${w.workshop_id}`} onClick={() => save(true)}>Clear date</Button>}
      </div>
      <div className="mt-6 text-[10px] uppercase tracking-[0.2em] font-adult-mono text-zinc-500">Enrolled</div>
      {w.enrollees.length === 0 ? <div className="mt-2 text-sm text-zinc-500">No enrolments yet.</div> : (
        <ul className="mt-2 divide-y divide-zinc-900 text-sm">
          {w.enrollees.map((p, i) => (
            <li key={`${p.email}-${i}`} className="py-2 flex justify-between gap-3"><span>{p.name || "—"} <span className="text-zinc-500">{p.email}</span></span><span className="text-xs text-zinc-500 font-adult-mono">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : ""}</span></li>
          ))}
        </ul>
      )}
    </div>
  );
}

const INVITE_COLS = ["created_at", "org_type", "org_name", "contact_name", "designation", "email", "phone", "location", "preferred_dates", "audience_size", "budget", "topic", "message"];

export function InvitesAdminTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/speaker-invites").then((r) => setRows(r.data)).catch(showErr); }, []);
  const exportCsv = () => {
    const csv = [INVITE_COLS.join(",")].concat(rows.map((r) => INVITE_COLS.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","))).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `speaker_invites_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };
  if (!rows.length) return <Empty testid="admin-invites-empty">No campus or workplace invites yet. Share <span className="text-amber-400 font-adult-mono">/invite-sankalp</span> with colleges and companies.</Empty>;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-400"><span className="text-amber-400 font-semibold">{rows.length}</span> invites</div>
        <button data-testid="admin-invites-export" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold px-4 py-2"><Download className="w-3.5 h-3.5" />Export CSV</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((r) => (
          <div key={r.invite_id} data-testid={`admin-invite-${r.invite_id}`} className="rounded-2xl border border-zinc-800 p-5 text-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="font-adult-heading text-lg">{r.org_name}</div>
              <span className="text-[10px] uppercase tracking-[0.16em] font-adult-mono rounded-full bg-violet-500/10 border border-violet-500/40 text-violet-300 px-2.5 py-1">{r.org_type}</span>
            </div>
            <div className="mt-1 text-zinc-300">{r.contact_name}{r.designation ? ` · ${r.designation}` : ""}</div>
            <div className="text-xs"><a href={`mailto:${r.email}`} className="text-amber-400 hover:underline">{r.email}</a> · <span className="text-zinc-500">{r.phone}</span></div>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {[["Location", r.location], ["Dates", r.preferred_dates], ["Audience", r.audience_size], ["Budget", r.budget], ["Topic", r.topic]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k}><dt className="text-zinc-500 inline">{k}: </dt><dd className="inline text-zinc-200">{v}</dd></div>
              ))}
            </dl>
            {r.message && <p className="mt-3 text-xs text-zinc-400 whitespace-pre-wrap">{r.message}</p>}
            <div className="mt-3 text-[10px] text-zinc-600 font-adult-mono">{new Date(r.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReelsSettingsCard() {
  const [reels, setReels] = useState([]);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/catalog/founder").then((r) => setReels(r.data.reels || []));
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    setBusy(true);
    try {
      await api.post("/admin/founder/settings", { instagram_reels: next });
      await load(); setUrl(""); toast.success("Reels updated");
    } catch (e) { showErr(e); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-zinc-800 p-6" data-testid="admin-reels-card">
      <div className="text-xs uppercase tracking-[0.2em] font-adult-mono text-zinc-500 mb-3 flex items-center gap-2"><Instagram className="w-3.5 h-3.5 text-pink-400" />Instagram reels on Meet Sankalp</div>
      <p className="text-sm text-zinc-400">Open a reel on Instagram, tap <span className="text-amber-400">Share → Copy link</span>, and paste it here. Reels show as a gallery at the bottom of the Meet Sankalp page.</p>
      <div className="mt-4 flex gap-2">
        <Input data-testid="admin-reel-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.instagram.com/reel/…" className={`${inputCls} font-adult-mono text-xs`} />
        <Button data-testid="admin-reel-add" disabled={busy || !url.trim()} onClick={() => save([...reels.map((r) => r.url), url.trim()])} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full"><Plus className="w-4 h-4 mr-1" />Add</Button>
      </div>
      {reels.length > 0 && (
        <ul className="mt-4 divide-y divide-zinc-900" data-testid="admin-reel-list">
          {reels.map((r) => (
            <li key={r.code} className="py-2 flex items-center justify-between gap-3">
              <a href={r.url} target="_blank" rel="noreferrer" className="text-xs font-adult-mono text-zinc-300 hover:text-amber-400 truncate">{r.url}</a>
              <Button size="icon" variant="ghost" disabled={busy} data-testid={`admin-reel-remove-${r.code}`} onClick={() => save(reels.filter((x) => x.code !== r.code).map((x) => x.url))} className="h-8 w-8 text-red-400 shrink-0"><Trash2 className="w-3.5 h-3.5" /></Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
